// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2, Play, Square, Trophy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { MirageEvalMetric, MirageEvalStatusBadge } from "~/modules/mirage-video-evals/components";
import { formatConfig, formatMirageEvalDate } from "~/modules/mirage-video-evals/formatters";
import {
  closeMirageVideoEvalTestMutation,
  openMirageVideoEvalTestMutation,
} from "~/modules/mirage-video-evals/mutations";
import { getMirageVideoEvalTestQuery } from "~/modules/mirage-video-evals/queries";

export const Route = createFileRoute("/_dash/videos/evals/$testId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { testId } = Route.useParams();
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const {
    data: test,
    isLoading,
    error,
    refetch,
  } = getMirageVideoEvalTestQuery.useQuery({
    pathParams: { testId },
  });

  const setOpen = async () => {
    setIsChangingStatus(true);
    try {
      await openMirageVideoEvalTestMutation.trigger({
        pathParams: { testId },
        params: {},
      });
      toast.success("Eval opened");
      await refetch();
    } catch (error) {
      toast.error("Failed to open eval", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsChangingStatus(false);
    }
  };

  const setClosed = async () => {
    setIsChangingStatus(true);
    try {
      await closeMirageVideoEvalTestMutation.trigger({
        pathParams: { testId },
        params: {},
      });
      toast.success("Eval closed");
      await refetch();
    } catch (error) {
      toast.error("Failed to close eval", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsChangingStatus(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading eval...</div>
    );
  }

  if (error || !test) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load Mirage eval</p>
        {error && <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals">Back to evals</Link>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">{test.name}</h1>
            <MirageEvalStatusBadge status={test.status} />
          </div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">{test.testId}</div>
          {test.description && (
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{test.description}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/evals/$testId/rank" params={{ testId }}>
              <Play className="size-4" />
              Rank
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/evals/$testId/results" params={{ testId }}>
              <Trophy className="size-4" />
              Results
            </Link>
          </Button>
          {test.status !== "open" ? (
            <Button onClick={setOpen} size="sm" disabled={isChangingStatus}>
              {isChangingStatus ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Play className="size-4" />
              )}
              Open
            </Button>
          ) : (
            <Button onClick={setClosed} size="sm" variant="secondary" disabled={isChangingStatus}>
              {isChangingStatus ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Square className="size-4" />
              )}
              Close
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <MirageEvalMetric label="Videos" value={test.videoCount} />
        <MirageEvalMetric label="Clips" value={test.clipCount} />
        <MirageEvalMetric label="Votes" value={test.voteCount} />
        <MirageEvalMetric label="Updated" value={formatMirageEvalDate(test.updatedAt)} />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Video</TableHead>
              <TableHead>Clip</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Config</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {test.videos.map((video) => (
              <TableRow key={video.videoId}>
                <TableCell className="font-mono text-xs">{video.videoId}</TableCell>
                <TableCell className="font-mono text-xs">{video.clipId}</TableCell>
                <TableCell className="font-mono text-xs">{video.assetId}</TableCell>
                <TableCell className="max-w-xl">
                  <pre className="max-h-28 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-xs">
                    {formatConfig(video.config)}
                  </pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
